import { Typography, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';

import TabPanel from 'components/tab-panel';
import { useAccountState } from 'contexts/account/account-context';
import { Starvation } from 'views/order-feed/features/starvation/types/starvation.type';
import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { useGet } from 'hooks/use-get';
import _ from 'underscore';
import NotificationsSubTabs from './components/notifications-sub-tabs';
import PlanningPrognosisTabsHeader from '../../components/planning-prognosis-tabs-header';
import { getStarvingPeriodActive, getStarvingPeriodHistoric } from 'views/order-feed/features/starvation/api/get-starvation';
import { usePagination } from 'hooks/use-pagination';
import Paper from 'components/paper';

type ParamTypes = {
  tab: Tabs;
};

enum Tabs {
  ACTIVE = 'active',
  HISTORIC = 'historic',
}

type Props = {};

const ListNotifications: React.FC<Props> = () => {
  const intl = useIntl();
  // const history = useHistory();
  const { params } = useRouteMatch<ParamTypes>();
  const { account } = useAccountState();
  const theme = useTheme();

  const PAGE_NAME_ID = 'planningPrognosis.listNotifications';
  const PAGINATION_LIMIT = 10;

  const listOptions: HeadCell[] = [
    { id: 'startDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.startDateLabel` }) },
    { id: 'endDate', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.endDateLabel` }) },
    { id: 'unitName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.unitNameLabel` }) },
    { id: 'fishGroupName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.fishGroupNameLabel` }) },
    { id: 'siteName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.siteNameLabel` }) },
    { id: 'comment', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.commentLabel` }) },
  ];

  // #region STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  // #endregion STATE

  // #region FETCH DATA

  const { data: active, isLoading: activeIsLoading } = useGet(
    ['getStarvingPeriodActive', account.company?.companyId],
    (context) => getStarvingPeriodActive(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId && params.tab === Tabs.ACTIVE,
    }
  );

  const { data: historic, isLoading: historicIsLoading } = useGet(
    ['getStarvingPeriodHistoric', account.company?.companyId],
    (context) => getStarvingPeriodHistoric(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId && params.tab === Tabs.HISTORIC,
    }
  );

  // #endregion FETCH DATA

  // #region HANDLERS

  // #endregion HANDLERS

  // #region RENDERS

  const renderRows = () => {
    let rows: Starvation[] = [];

    switch (params.tab) {
      case Tabs.ACTIVE:
        rows = _.chain(active)
          .filter((p) => p)
          .value();
        break;
      case Tabs.HISTORIC:
        rows = _.chain(historic)
          .filter((p) => p)
          .value();
        break;

      default:
        break;
    }

    return _.chain(rows)
      .sortBy((p) => p.startDate)
      .value();
  };

  // #endregion HANDLERS

  return (
    <>
      <PlanningPrognosisTabsHeader utils={<Breadcrumbs />} />

      <Typography variant="h3" color={theme.palette.common.white} mb={5}>
        <FormattedMessage id="orderFeed.listStarvation.title" />
      </Typography>
      <NotificationsSubTabs />
      <Paper transparent marginBottom={0}>
        <TabPanel value={params.tab} index={Tabs.ACTIVE}>
          <List
            options={listOptions}
            isLoading={activeIsLoading}
            noResult={{
              show: !!active && active?.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {active?.slice(from, to).map((row, idx) => (
              <ListRow key={idx}>
                <ListCell align="left">
                  <FormattedDate value={row.startDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell align="left">
                  <FormattedDate value={row.endDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell align="left">{row.unitName}</ListCell>
                <ListCell align="left">{row.fishGroupName}</ListCell>
                <ListCell align="left">{row.siteName}</ListCell>
                <ListCell align="left">{row.comment}</ListCell>
              </ListRow>
            ))}
          </List>
        </TabPanel>
        <TabPanel value={params.tab} index={Tabs.HISTORIC}>
          <List
            options={listOptions}
            isLoading={historicIsLoading}
            noResult={{
              show: !!historic && historic?.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {historic?.slice(from, to).map((row, idx) => (
              <ListRow key={idx}>
                <ListCell align="left">
                  <FormattedDate value={row.startDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell align="left">
                  <FormattedDate value={row.endDate} day="2-digit" month="short" year="numeric" />
                </ListCell>
                <ListCell align="left">{row.unitName}</ListCell>
                <ListCell align="left">{row.fishGroupName}</ListCell>
                <ListCell align="left">{row.siteName}</ListCell>
                <ListCell align="left">{row.comment}</ListCell>
              </ListRow>
            ))}
          </List>
        </TabPanel>
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default ListNotifications;
